import { useEffect, useState } from 'react';
import { apiClient } from '@/sdk/shared/apiClient';
import { getApiBaseUrl } from '@/lib/env';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Robust fetch that supports ngrok headers and automatically falls back to same-origin Next.js /api proxy
async function fetchPushApi(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = apiClient.getToken();
  const baseHeaders: Record<string, string> = {
    'ngrok-skip-browser-warning': 'true',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const finalHeaders = {
    ...baseHeaders,
    ...((options.headers as Record<string, string>) || {}),
  };

  const primaryUrl = `${getApiBaseUrl()}${endpoint}`;
  try {
    const res = await fetch(primaryUrl, {
      ...options,
      headers: finalHeaders,
    });
    return res;
  } catch (primaryErr) {
    console.warn(`[Push] Fetch to ${primaryUrl} failed (${(primaryErr as any)?.message}). Retrying via relative /api proxy...`);
    // Fallback to same-origin relative URL (proxied directly by Next.js rewrites)
    const fallbackUrl = `/api${endpoint}`;
    return fetch(fallbackUrl, {
      ...options,
      headers: finalHeaders,
    });
  }
}

export interface PushPlatformCapabilities {
  isSupported: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  needsHomeInstallation: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  loading: boolean;
  testing: boolean;
  requestPermissionAndSubscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  sendTestNotification: () => Promise<{ success: boolean; message: string }>;
  checkStatus: () => Promise<void>;
}

export function usePushNotifications(): PushPlatformCapabilities {
  const [isSupported, setIsSupported] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [needsHomeInstallation, setNeedsHomeInstallation] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  // Platform detection and initial capability assessment
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();
    const isAppleDevice = /iphone|ipad|ipod/.test(ua) || 
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    
    // Standalone PWA detection (Safari navigator.standalone or display-mode: standalone media query)
    const isAppStandalone = 
      (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches;

    const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    setIsIOS(isAppleDevice);
    setIsStandalone(isAppStandalone);
    setIsSupported(pushSupported);

    // On iOS, if not in standalone PWA mode and PushManager is absent, the user must add to Home Screen
    if (isAppleDevice && (!isAppStandalone || !pushSupported)) {
      setNeedsHomeInstallation(true);
    } else {
      setNeedsHomeInstallation(false);
    }

    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Listen for service worker updates taking control (skipWaiting + clients.claim)
    let refreshing = false;
    const handleControllerChange = () => {
      if (refreshing) return;
      if (window.location.pathname.startsWith('/admin')) return;

      const lastReload = sessionStorage.getItem('sw_reload_ts');
      const now = Date.now();
      // Guard against infinite reload loops: wait at least 15 seconds
      if (!lastReload || now - Number(lastReload) > 15000) {
        refreshing = true;
        sessionStorage.setItem('sw_reload_ts', String(now));
        window.location.reload();
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    // Verify existing subscription if supported
    if (pushSupported) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.pushManager.getSubscription().then((subscription) => {
          setIsSubscribed(!!subscription);
        }).catch((err) => {
          console.warn('[Push] Error getting initial subscription:', err);
        });
      }).catch((err) => {
        console.warn('[Push] Service worker ready check failed:', err);
      });
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  const checkStatus = async () => {
    if (typeof window === 'undefined') return;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        if ('Notification' in window) {
          setPermission(Notification.permission);
        }
      } catch (err) {
        console.warn('[Push] Status check failed:', err);
      }
    }
  };

  const requestPermissionAndSubscribe = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;

    // Direct guidance if user is in an iOS browser tab without standalone PWA mode
    if (needsHomeInstallation) {
      console.warn('[Push] Action blocked: Web Push on iOS requires adding the app to the Home Screen.');
      return false;
    }

    if (!isSupported) {
      console.warn('[Push] Push notifications are not supported in this browser context.');
      return false;
    }

    setLoading(true);
    try {
      // 1. Request Notification permission
      console.log('[Push] Step 1: Requesting notification permission...');
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        console.log('[Push] Notification permission not granted:', result);
        setLoading(false);
        return false;
      }

      // 2. Register Service Worker
      console.log('[Push] Step 2: Registering service worker /sw.js...');
      let registration: ServiceWorkerRegistration;
      try {
        registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        await navigator.serviceWorker.ready;
      } catch (swErr) {
        console.error('[Push] Service Worker registration failed:', swErr);
        throw swErr;
      }

      // 3. Get VAPID public key from backend
      console.log('[Push] Step 3: Fetching VAPID public key from backend...');
      const keyRes = await fetchPushApi('/notifications/vapid-public-key', {
        method: 'GET',
      });

      if (!keyRes.ok) {
        throw new Error(`Failed to fetch VAPID public key (HTTP ${keyRes.status})`);
      }

      const keyData = await keyRes.json();
      const vapidPublicKey = keyData.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID public key returned from backend is empty');
      }

      // 4. Subscribe client via PushManager
      console.log('[Push] Step 4: Subscribing user via PushManager...');
      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);
      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey
        });
      } catch (subErr) {
        console.error('[Push] pushManager.subscribe failed:', subErr);
        throw subErr;
      }

      // 5. Send subscription descriptor to backend
      console.log('[Push] Step 5: Registering subscription on backend...');
      const deviceType = isIOS ? 'mobile' : undefined;
      const subRes = await fetchPushApi('/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          subscription,
          deviceType
        })
      });

      if (!subRes.ok) {
        throw new Error(`Failed to save subscription on backend (HTTP ${subRes.status})`);
      }

      console.log('[Push] Subscription saved successfully!');
      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('[Push] Error subscribing to push notifications:', error);
      setLoading(false);
      return false;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    if (!isSupported) return false;

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // 1. Notify backend to deactivate/delete subscription
        await fetchPushApi('/notifications/unsubscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });

        // 2. Unsubscribe locally in browser
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('[Push] Error unsubscribing:', error);
      setLoading(false);
      return false;
    }
  };

  const sendTestNotification = async (): Promise<{ success: boolean; message: string }> => {
    setTesting(true);
    try {
      const res = await fetchPushApi('/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await res.json().catch(() => ({}));
      setTesting(false);

      if (res.ok && data.success) {
        return { success: true, message: data.message || 'Test notification dispatched successfully!' };
      } else {
        return { success: false, message: data.message || `Test failed with status ${res.status}` };
      }
    } catch (err: any) {
      setTesting(false);
      return { success: false, message: err?.message || 'Network error sending test notification' };
    }
  };

  return {
    isSupported,
    isIOS,
    isStandalone,
    needsHomeInstallation,
    permission,
    isSubscribed,
    loading,
    testing,
    requestPermissionAndSubscribe,
    unsubscribe,
    sendTestNotification,
    checkStatus
  };
}
